import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, IconButton, Paper, Dialog as MuiDialog, Slide, DialogActions, DialogContent, DialogTitle, Button
} from '@mui/material';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { AuthContext } from './Auth';
import EventDialog from './EventDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';

function EventDay(props) {
  const { day, events, ...other } = props;
  return (
    <Box sx={{ position: 'relative', width: 36, height: 36 }}>
      <PickersDay {...other} day={day} />
      {events && events.length > 0 && (
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 4, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: events[0].color || '#1976d2' }} />
        </Box>
      )}
    </Box>
  );
}

function EventDetailDialog({ open, onClose, event }) {
  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          pb: 2,
          position: 'fixed',
          left: { xs: 0, sm: '20%' },
          right: { xs: 0, sm: '20%' },
          bottom: 0,
          m: 0,
          width: { xs: '100vw', sm: '60vw' },
          maxWidth: { xs: '100vw', sm: '60vw' },
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        },
      }}
    >
      <Box sx={{ p: 2, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Event Details</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ p: 2, pt: 0 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{event?.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{event?.description}</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {event && new Date(event.start.seconds ? event.start.seconds * 1000 : event.start).toLocaleString()} - {event && new Date(event.end.seconds ? event.end.seconds * 1000 : event.end).toLocaleString()}
        </Typography>
        {event?.location && <Typography variant="body2" color="text.secondary">{event.location}</Typography>}
      </Box>
    </MuiDialog>
  );
}

export default function EventsCalendar({ channelId, isOwner }) {
  const { authUser, firestoreInstance } = useContext(AuthContext);
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  // Add currentMonth state to track the visible month
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [monthEvents, setMonthEvents] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  useEffect(() => {
    if (!firestoreInstance || !channelId) return;
    // Use currentMonth for monthStart and monthEnd
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    const q = query(
      collection(firestoreInstance, 'channels', channelId, 'events'),
      where('start', '>=', monthStart),
      where('start', '<=', monthEnd),
      orderBy('start', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      const byDay = {};
      snap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const start = data.start.seconds ? new Date(data.start.seconds * 1000) : new Date(data.start);
        // Use local date string for grouping (YYYY-MM-DD in local time)
        const dayKey = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}`;
        if (!byDay[dayKey]) byDay[dayKey] = [];
        byDay[dayKey].push({ id: docSnap.id, ...data });
      });
      setMonthEvents(byDay);
    });
    return () => unsub();
  }, [firestoreInstance, channelId, currentMonth]);

  const handleAdd = () => {
    setEditingEvent(null);
    setDialogOpen(true);
  };
  const handleEdit = (event) => {
    setEditingEvent(event);
    setDialogOpen(true);
  };
  const handleDelete = async (eventId) => {
    setEventToDelete(eventId);
    setDeleteConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (eventToDelete) {
      await deleteDoc(doc(firestoreInstance, 'channels', channelId, 'events', eventToDelete));
      setDeleteConfirmOpen(false);
      setEventToDelete(null);
    }
  };
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEventToDelete(null);
  };

  // In DateCalendar, also use local date string for key lookup
  const selectedKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;
  const eventsForSelected = monthEvents[selectedKey] || [];

  return (
    <Box>
      <DateCalendar
        value={selectedDate}
        onChange={setSelectedDate}
        // Add onMonthChange to update currentMonth
        onMonthChange={date => setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1))}
        slots={{ day: (props) => (
          <EventDay {...props} events={monthEvents[`${props.day.getFullYear()}-${String(props.day.getMonth()+1).padStart(2,'0')}-${String(props.day.getDate()).padStart(2,'0')}`] || []} />
        ) }}
      />
      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">Events for {selectedDate.toLocaleDateString()}</Typography>
        {isOwner && <IconButton color="primary" onClick={handleAdd}><AddCircleIcon /></IconButton>}
      </Box>
      <Box>
        {eventsForSelected.length === 0 && <Typography color="text.secondary">No events for this day.</Typography>}
        {eventsForSelected.map(event => (
          <Paper
            key={event.id}
            sx={{ p: 1, mb: 1, bgcolor: event.color || '#e3f2fd', cursor: 'pointer' }}
            onClick={() => { setSelectedEvent(event); setDetailDialogOpen(true); }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}
              >
                {event.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}
              >
                {event.description}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {event.allDay ? 'All Day' : `${new Date(event.start.seconds ? event.start.seconds * 1000 : event.start).toLocaleTimeString()} - ${new Date(event.end.seconds ? event.end.seconds * 1000 : event.end).toLocaleTimeString()}`}
                </Typography>
                {isOwner && (
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: 1 }}>
                    <IconButton size="small" color="primary" sx={{ mr: 0.5 }} onClick={e => { e.stopPropagation(); handleEdit(event); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); handleDelete(event.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
      <EventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        event={editingEvent}
        channelId={channelId}
        firestoreInstance={firestoreInstance}
        authUser={authUser}
      />
      <EventDetailDialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} event={selectedEvent} />
      {/* Delete confirmation dialog */}
      <MuiDialog open={deleteConfirmOpen} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this event?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </MuiDialog>
    </Box>
  );
}
